import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Lightweight tooltip primitives that mirror the Radix Tooltip API
 * but use CSS-only hover tooltips (no extra dependency needed).
 */

function TooltipProvider({ children }: { children: React.ReactNode; delayDuration?: number }) {
  return <>{children}</>;
}

function Tooltip({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("group/tooltip relative inline-flex", className)}>{children}</div>;
}

const TooltipTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & { asChild?: boolean }
>(({ asChild, children, ...props }, ref) => {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      ref,
      ...props,
    });
  }
  return (
    <span ref={ref as React.Ref<HTMLSpanElement>} {...props}>
      {children}
    </span>
  );
});
TooltipTrigger.displayName = "TooltipTrigger";

function TooltipContent({
  children,
  side = "top",
  className,
  ...props
}: {
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-1.5",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-1.5",
    left: "right-full top-1/2 -translate-y-1/2 mr-1.5",
    right: "left-full top-1/2 -translate-y-1/2 ml-1.5",
  };

  return (
    <div
      className={cn(
        "ds-caption pointer-events-none absolute z-50 hidden whitespace-nowrap rounded-md bg-primary px-3 py-1.5 text-primary-foreground shadow-md group-hover/tooltip:block",
        positionClasses[side],
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
